import type { AppStore } from '../core/store';
import { canApplyPwaUpdate, connectivityState, isStandaloneDisplay } from './pwaState';

export interface BeforeInstallPromptEvent extends Event {
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

type ApplyUpdate = () => Promise<void>;

const required = <T extends Element = HTMLElement>(root: ParentNode, selector: string): T => {
  const element = root.querySelector<T>(selector);
  if (!element) throw new Error(`No se encontró ${selector}`);
  return element;
};

export class PwaController {
  private installPrompt: BeforeInstallPromptEvent | null = null;
  private applyUpdate: ApplyUpdate | null = null;
  private offlineReady = false;
  private online: boolean;
  private readonly connectivity: HTMLElement;
  private readonly connectivityLabel: HTMLElement;
  private readonly installButton: HTMLButtonElement;
  private readonly offlineBasemapNotice: HTMLElement;
  private readonly updateToast: HTMLElement;
  private readonly updateNow: HTMLButtonElement;
  private readonly updateNote: HTMLElement;

  constructor(
    private readonly store: AppStore,
    root: ParentNode = document,
    private readonly browserWindow: Window = window,
    private readonly onConnectivityRestored: () => void = () => undefined,
  ) {
    this.online = browserWindow.navigator.onLine;
    this.connectivity = required(root, '#connectivity-status');
    this.connectivityLabel = required(root, '#connectivity-label');
    this.installButton = required(root, '#install-pwa');
    this.offlineBasemapNotice = required(root, '#offline-basemap-notice');
    this.updateToast = required(root, '#pwa-update-toast');
    this.updateNow = required(root, '#pwa-update-now');
    this.updateNote = required(root, '#pwa-update-note');

    browserWindow.addEventListener('online', () => this.setOnline(true));
    browserWindow.addEventListener('offline', () => this.setOnline(false));
    browserWindow.addEventListener('beforeinstallprompt', (event) => this.captureInstallPrompt(event as BeforeInstallPromptEvent));
    browserWindow.addEventListener('appinstalled', () => this.clearInstallPrompt());
    this.installButton.addEventListener('click', () => void this.install());
    this.updateNow.addEventListener('click', () => void this.update());
    required(root, '#pwa-update-later').addEventListener('click', () => { this.updateToast.hidden = true; });
    store.subscribe(() => this.render());
    this.render();
  }

  offerUpdate(apply: ApplyUpdate): void {
    this.applyUpdate = apply;
    this.updateToast.hidden = false;
    this.render();
  }

  setOfflineReady(): void {
    this.offlineReady = true;
    this.render();
  }

  private setOnline(online: boolean): void {
    const restored = !this.online && online;
    this.online = online;
    if (restored) this.onConnectivityRestored();
    this.render();
  }

  private captureInstallPrompt(event: BeforeInstallPromptEvent): void {
    event.preventDefault();
    if (this.isStandalone()) return;
    this.installPrompt = event;
    this.render();
  }

  private clearInstallPrompt(): void { this.installPrompt = null; this.render(); }

  private isStandalone(): boolean {
    const nav = this.browserWindow.navigator as Navigator & { standalone?: boolean };
    return isStandaloneDisplay(this.browserWindow.matchMedia('(display-mode: standalone)'), nav.standalone === true);
  }

  private async install(): Promise<void> {
    const prompt = this.installPrompt;
    if (!prompt || this.isStandalone()) return;
    await prompt.prompt();
    await prompt.userChoice;
    this.clearInstallPrompt();
  }

  private async update(): Promise<void> {
    const apply = this.applyUpdate;
    if (!apply) return;
    if (!canApplyPwaUpdate(this.store.getState().projectDirty)) {
      this.updateNote.textContent = 'Guarda o descarta los cambios del proyecto antes de actualizar.';
      this.updateNote.hidden = false;
      return;
    }
    this.updateNow.disabled = true;
    this.updateNote.textContent = 'Actualizando GeoStudio…';
    this.updateNote.hidden = false;
    try { await apply(); }
    catch {
      this.updateNow.disabled = false;
      this.updateNote.textContent = 'No fue posible actualizar. Puedes intentarlo nuevamente.';
    }
  }

  private render(): void {
    const state = this.store.getState();
    const status = connectivityState(this.online, state.basemap, state.presentationWorkspace.showBasemap);
    this.connectivity.dataset.online = String(status.online);
    this.connectivityLabel.textContent = status.label;
    this.connectivity.title = this.offlineReady
      ? 'GeoStudio está listo para abrirse sin conexión. Los mapas base remotos requieren Internet.'
      : status.online
      ? 'Con conexión. Los archivos CASUR continúan procesándose únicamente en este dispositivo.'
      : 'Sin conexión. GeoStudio y los datos locales siguen disponibles; los mapas base remotos pueden no cargar.';
    this.offlineBasemapNotice.hidden = !status.remoteBasemapUnavailable;
    this.installButton.hidden = !this.installPrompt || this.isStandalone();
    const dirty = state.projectDirty;
    this.updateNow.setAttribute('aria-disabled', String(dirty));
    if (this.applyUpdate && dirty) {
      this.updateNote.textContent = 'Hay cambios sin guardar. La actualización permanecerá en espera.';
      this.updateNote.hidden = false;
    } else if (this.applyUpdate) {
      this.updateNote.hidden = true;
    }
  }
}
